<template lang="pug">
  a(
    :class="[$style.cardContainer, {...hoverStyles}]"
    :href="linkPath"
    style="text-decoration: none;"
  )
    div(:class="[$style.categoryCard, {'mt-ds-ml': imagePeeksOut}]")
      div(:class="[$style.imageFrame, {'-mt-ds-ml': imagePeeksOut}]")
        div(:class="[$style.hover]")
          div(:class="[isUk ? $style.message : $style.messageNonUk]")
            span(:class="[{[$style.enlargedFontSize]: imagePeeksOut}, 'text-base-dark']") {{ hoverText }}
            Icon(
              class="ml-0 sm:ml-ds-sm md:ml-ds-md text-base-dark"
              :style="$i18n.locale === 'es' ? 'display: none;': ''"
              :size="imagePeeksOut ? 32 : 24"
              name="i:chevron-right-thick"
            )
        slot
      div(:class="[$style.categoryName]")
        div(:class="[$style.categoryNameContainer]")
          p(
            class="text-base-dark m-0 xs:pr-ds-md"
            v-html="title")
          div(class="flex sm:hidden justify-self-end relative top-[1px]")
            Icon(
              name="i:chevron-right-thick"
              class="text-base-dark"
              :size="12")
          div(class="hidden sm:flex xl:hidden justify-self-end relative top-[1px]")
            Icon(
              name="i:chevron-right-thick"
              class="text-base-dark"
              :size="16")
          div(class="hidden xl:flex justify-self-end relative top-[1px]")
            Icon(
              name="i:chevron-right-thick"
              class="text-base-dark"
              :size="18")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { LOCALE } from "~/constants/locale"

export default defineComponent({
  name: "CategoryCard",
  props: {
    title: {
      type: String,
      required: true
    },
    linkPath: {
      type: String,
      required: true
    },
    imagePeeksOut: {
      type: Boolean,
      default: false
    },
    instantPrices: {
      type: Boolean,
      default: false
    },
    animateFrom: {
      type: String as () => "right" | "bottom",
      default: "right"
    }
  },
  data() {
    return {
      isIOS: false
    }
  },
  computed: {
    isUk() {
      return this.$i18n.locale === LOCALE.UK
    },
    hoverText() {
      return this.instantPrices
        ? this.$t("home_card_header.get_instant_prices")
        : this.$t("home_card_header.get_quote")
    },
    hoverStyles() {
      if (this.isIOS) {
        return {}
      }
      return {
        "animate-from-bottom": this.animateFrom === "bottom",
        "animate-from-right": this.animateFrom === "right"
      }
    }
  },
  mounted() {
    this.detectIOS()
  },
  methods: {
    detectIOS() {
      if (typeof window !== "undefined") {
        const userAgent = window.navigator.userAgent.toLowerCase()
        this.isIOS =
          /iphone|ipad|ipod/.test(userAgent) ||
          (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
      }
    }
  }
})
</script>

<style lang="css" module>
.cardContainer {
  @apply flex flex-col relative overflow-hidden;
  transition: transform 200ms ease-in-out;

  /* Apply hover styles only for devices that support hover */
  @media (hover: hover) {
    &:hover p {
      text-decoration: #7ed321;
    }
  }

  &:not(:global(.animate-from-bottom)) {
    .categoryCard {
      @apply overflow-hidden;
    }
  }

  .categoryCard {
    @apply h-full flex flex-col flex-nowrap bg-anyvan-yellow active:bg-anyvan-yellow hover:bg-anyvan-yellow-soft rounded-xl md:rounded-2xl;
    transition: background-color 200ms ease-in-out;
  }

  .imageFrame {
    @apply relative overflow-hidden;
  }

  figure {
    @apply relative w-full self-end flex justify-center z-10;
    flex-grow: 1;
    animation-duration: 200ms;
    animation-timing-function: ease-in-out;
    animation-fill-mode: forwards;
  }

  &:global(.animate-from-right) figure {
    animation-name: slide-left;
  }

  &:global(.animate-from-bottom) figure {
    animation-name: slide-up;
  }

  .icon {
    display: none;
  }

  .categoryName {
    @apply flex-1 flex py-ds-xxs px-ds-xxs sm:px-ds-sm rounded-b-xl md:rounded-b-2xl w-full bg-white z-20;

    .categoryNameContainer {
      @apply relative grid w-full items-center;
      grid-template-columns: 10px 1fr 10px;

      p {
        @apply justify-self-center text-base sm:text-rebrandSm xl:text-rebrandMd font-bold;
        grid-column: 2/3;
      }
    }
  }

  .hover {
    @apply flex flex-col h-full opacity-0 absolute justify-center;
    inset: 0 0 50px 0;
    transition: opacity 200ms ease-in-out;

    .message {
      @apply flex items-center justify-between text-base sm:text-rebrandSm xl:text-rebrandMd leading-none font-bold;
      z-index: 1;
      width: 50px;
      padding-left: 15%;

      .enlargedFontSize {
        font-size: 1.2em;
      }
    }

    .messageNonUk {
      @apply flex items-center justify-between text-xs sm:text-rebrandSm xl:text-rebrandMd leading-none font-bold;
      z-index: 1;
      width: 50px;
      padding-left: 15%;

      .enlargedFontSize {
        font-size: 1.2em;
      }
    }
  }

  /* Move all hover effects inside this media query */
  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      transform: scale(1.05);

      &:global(.animate-from-right) {
        figure {
          animation: slide-out-right;
          animation-duration: 200ms;
          animation-fill-mode: forwards;
        }

        .hover {
          opacity: 1;
          animation: slide-right;
          animation-duration: 200ms;
          animation-fill-mode: forwards;
        }
      }

      &:global(.animate-from-bottom) {
        figure {
          animation: slide-out-down;
          animation-duration: 200ms;
          animation-fill-mode: forwards;
        }

        .hover {
          animation: slide-down;
          animation-duration: 200ms;
          animation-fill-mode: forwards;
        }
      }
    }
  }
}

@keyframes slide-up {
  0% {
    transform: translateY(450px);
  }
  100% {
    transform: translateY(0);
  }
}

@keyframes slide-down {
  0% {
    transform: translateY(-450px);
    opacity: 0;
  }
  50% {
    opacity: 0;
  }
  60% {
    opacity: 1;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slide-left {
  from {
    transform: translateX(450px);
  }
  to {
    transform: translateX(0px);
  }
}

@keyframes slide-right {
  from {
    transform: translateX(-450px);
  }
  to {
    transform: translateX(0px);
  }
}

@keyframes slide-out-right {
  from {
    transform: translateX(0px);
  }
  to {
    transform: translateX(450px);
  }
}

@keyframes slide-out-down {
  from {
    transform: translateX(0px);
  }
  to {
    transform: translateY(450px);
  }
}
</style>
